import { type MouseEventHandler } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import ArrowTopIcon from '@assets/img/icons/arrow-top.svg?react';
import { AppRoute } from '@common/app/routes';
import {
  useCheckPlantsCheck,
  useCheckRequirements,
} from '@common/hooks/check-requirements';
import {
  type UserFactory,
  type UserFactoryNextToBuild,
} from '@common/types/factoryType';
import { type PlantTypeText } from '@common/types/plant';
import {
  type AvailableQuarry,
  type UserQuarry,
} from '@common/types/quarryType';
import { type ResourceTypeText } from '@common/types/resource';
import { ResourceItem } from '@components/ResourceItem/ResourceItem';
import { StringXSmall } from '@components/Typography/Typography';
import { StorageIndicator } from '@components/ui/StorageIndicator/StorageIndicator';
import cn from 'classnames';

import { resourceList } from '@/common/constants';

import { QuarryItem } from './QuarryItem/QuarryItem';

import css from './styles.module.scss';

import lockSvg from '/img/icons/lock-icon.svg';

interface ResourceNavigationItemProps<
  T extends ResourceTypeText | PlantTypeText,
> {
  resource: T;
  data?: T extends ResourceTypeText ? UserQuarry : UserFactory;
  nextToBuild?: T extends ResourceTypeText
    ? AvailableQuarry
    : UserFactoryNextToBuild;
  isAnimationStart?: boolean;
}

const ResourceNavigationItem = <T extends ResourceTypeText | PlantTypeText>({
  data,
  resource,
  nextToBuild,
  isAnimationStart,
}: ResourceNavigationItemProps<T>): JSX.Element => {
  const { id } = useParams();
  const navigate = useNavigate();
  const upgradeRequirements = useCheckRequirements(data?.nextLevelRequirements);
  const creationRequirements = useCheckRequirements(
    nextToBuild?.creationRequirements,
  );
  const isStartedMineAvailable =
    nextToBuild?.name === resource && creationRequirements.isComplete;

  const completePlants = useCheckPlantsCheck(
    nextToBuild?.creationRequirements?.plants,
  );

  const goToBuild = (): void => {
    if (nextToBuild?.name === resource && completePlants) {
      navigate(
        resourceList.includes(resource as ResourceTypeText)
          ? AppRoute.RESOURCES + '/build'
          : AppRoute.FACTORY + '/build',
        { state: { isFirstBuild: true } },
      );
    }
  };

  const link = resourceList.includes(resource as ResourceTypeText)
    ? `${AppRoute.RESOURCES}/mineResource/${data?.id}`
    : `${AppRoute.FACTORY}/${data?.id}`;

  const disabledNav: MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  return data ? (
    <NavLink
      onClick={isAnimationStart ? disabledNav : undefined}
      className="shrink-0"
      to={link}
      replace={true}
    >
      {({ isActive }) =>
        resourceList.includes(resource as ResourceTypeText) ? (
          <QuarryItem
            isActive={isActive}
            id={id}
            name={resource as ResourceTypeText}
            level={data.level}
            upgradeRequirements={upgradeRequirements}
          />
        ) : (
          <ResourceItem
            status={isActive || id === data.name ? 'active' : undefined}
            variant="light"
            radius="default"
            key={data.id}
            size={{ width: '5rem', height: '5rem' }}
            kind={data.name}
          >
            {data.level && (
              <span className={css.level}>
                <StringXSmall weight="bold" className="text-white/60">
                  {data.level}
                </StringXSmall>
              </span>
            )}
            {upgradeRequirements.isComplete && (
              <ArrowTopIcon className={css.upgrade} />
            )}
            <StorageIndicator
              className="absolute right-[0.25rem] bottom-[0.25rem]"
              resource={data.name}
            />
          </ResourceItem>
        )
      }
    </NavLink>
  ) : (
    <ResourceItem
      onClick={goToBuild}
      className={cn('shrink-0', isStartedMineAvailable && 'cursor-pointer')}
      variant="light"
      radius="default"
      overlay={nextToBuild?.name === resource && completePlants ? null : 'dark'}
      size={{ width: '5rem', height: '5rem' }}
      kind={resource}
    >
      {nextToBuild?.name === resource && completePlants ? (
        <ArrowTopIcon
          className={cn(
            css.upgrade,
            creationRequirements.isComplete ? 'text-accent' : 'text-white/40',
          )}
        />
      ) : (
        <div className="card-resource lock-Resource">
          <img src={lockSvg} width={24} alt="lock" />
        </div>
      )}
    </ResourceItem>
  );
};

export { ResourceNavigationItem };
